import type {Line} from '@Utils/math.util';
import {snapItemToLines} from '@PosterWhiteboard/page/snap-items';
import type {CanvasEvents, TMat2D} from '@postermywall/fabricjs-2';
import {Canvas} from '@postermywall/fabricjs-2';
import type {Page} from './page.class';

const GRID_STROKE = 'rgba(192,192,192, 0.5)';
const THIN_GRID_LINE_WIDTH = 1;
const THICK_GRID_LINE_WIDTH = 2;
const MAJOR_GRID_INTERVAL = 5;
const MAX_SNAPPING_THRESHOLD = 7;

export class RenderGrid {
  public page: Page;
  private canvasCleared = false;
  private zoom = 1;
  private viewportTransform: TMat2D;

  constructor(page: Page) {
    this.page = page;
    this.viewportTransform = this.page.fabricCanvas.viewportTransform;
    if (this.page.fabricCanvas instanceof Canvas) {
      this.initAligningGuidelines();
    }
  }

  private initAligningGuidelines(): void {
    this.page.fabricCanvas.on('mouse:down', this.onMouseDown.bind(this));
    this.page.fabricCanvas.on('object:moving', this.onObjectMoving.bind(this));
    this.page.fabricCanvas.on('before:render', this.beforeRender.bind(this));
    this.page.fabricCanvas.on('after:render', this.afterRender.bind(this));
  }

  private beforeRender(): void {
    if (this.page.fabricCanvas instanceof Canvas && this.page.fabricCanvas.contextTop) {
      this.page.fabricCanvas.contextTopDirty = true;
      this.canvasCleared = true;
    }
  }

  private afterRender(): void {
    this.drawGridLines();
  }

  private onMouseDown(): void {
    this.viewportTransform = this.page.fabricCanvas.viewportTransform;
    this.zoom = this.page.fabricCanvas.getZoom();
  }

  private drawGridLines(): void {
    if (this.page.poster.grid.enabled && this.canvasCleared) {
      const ctx = this.page.fabricCanvas.contextContainer;
      const offsetX = this.page.fabricCanvas.viewportTransform[4];
      const offsetY = this.page.fabricCanvas.viewportTransform[5];
      const zoom = this.page.fabricCanvas.getZoom();
      const width = this.page.poster.width * zoom;
      const height = this.page.poster.height * zoom;
      const gridSpacing = this.page.poster.grid.size * zoom;
      if (!Number.isFinite(gridSpacing) || gridSpacing <= 0) {
        this.canvasCleared = false;
        return;
      }
      const gridLenX = width / gridSpacing;
      const gridLenY = height / gridSpacing;

      for (let i = 0; i < gridLenY; i++) {
        const y = i * gridSpacing + offsetY;
        this.strokeGridLine(ctx, offsetX, y, width + offsetX, y, i % MAJOR_GRID_INTERVAL === 0);
      }
      for (let i = 0; i < gridLenX; i++) {
        const x = i * gridSpacing + offsetX;
        this.strokeGridLine(ctx, x, offsetY, x, height + offsetY, i % MAJOR_GRID_INTERVAL === 0);
      }
      this.canvasCleared = false;
    }
  }

  private strokeGridLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, isMajor: boolean): void {
    ctx.save();
    ctx.lineWidth = isMajor ? THICK_GRID_LINE_WIDTH : THIN_GRID_LINE_WIDTH;
    ctx.strokeStyle = GRID_STROKE;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  }

  private getMajorGridLines(): Line[] {
    const lines: Line[] = [];
    const zoom = this.page.fabricCanvas.getZoom();
    const width = this.page.poster.width * zoom;
    const height = this.page.poster.height * zoom;
    const gridSpacing = this.page.poster.grid.size * zoom;
    const majorSpacing = gridSpacing * MAJOR_GRID_INTERVAL;
    if (!Number.isFinite(majorSpacing) || majorSpacing <= 0) {
      return lines;
    }
    const gridLenX = width / majorSpacing;
    const gridLenY = height / majorSpacing;

    for (let i = 0; i < gridLenY; i++) {
      const distance = i * majorSpacing;
      lines.push({x1: 0, y1: distance, x2: width, y2: distance});
    }
    for (let i = 0; i < gridLenX; i++) {
      const distance = i * majorSpacing;
      lines.push({x1: distance, y1: 0, x2: distance, y2: height});
    }

    return lines;
  }

  private onObjectMoving(e: CanvasEvents['object:moving']): void {
    if (this.page.poster.grid.enabled && this.page.fabricCanvas instanceof Canvas) {
      const activeObject = e.target;
      if (!activeObject) {
        return;
      }
      // TODO: This shouldn't be accessing a private property

      const transform = this.page.fabricCanvas._currentTransform;

      if (!transform) return;

      snapItemToLines(activeObject, this.getMajorGridLines(), this.getSnappingThreshold(), this.viewportTransform, this.zoom);
    }
  }

  private getSnappingThreshold(): number {
    return Math.min(Math.ceil(this.page.poster.grid.size / 10), MAX_SNAPPING_THRESHOLD);
  }
}
